/**
 * One-line explanations for ticket form fields, looked up by the field's own
 * label. FieldGroup resolves this itself, so a field gets its hover text just
 * by being labelled — no call site has to pass anything.
 *
 * Keep every line short, plain and concrete: what the field is for, in the
 * words someone filling in a ticket would use. No jargon, no restating the
 * label back at the reader ("The type of the ticket" helps nobody).
 */
const HELP: Record<string, string> = {
  // What happened
  'Title': 'A short summary, like a subject line. This is what people see in the list.',
  'Subject': 'A short summary of what you need. This is what our team sees first.',
  'Description': 'What happened, what you expected, and anything you already tried.',
  'Tags': 'Your own keywords, for searching later.',
  'Attachments': 'Screenshots or files. You can paste an image straight into the description.',

  // Classification
  'Type': 'What kind of request this is — a broken thing, a question, a new request.',
  'Priority': 'How urgent this is. It sets the response and fix deadlines.',
  'Category': 'Your own grouping, used for reporting.',
  'Source': 'How this reached you — a phone call, an email, someone stopping by.',
  'Business impact': 'How many people this affects — one person, a team, the whole company.',
  'Environment': 'Which system this happened on, if you run more than one.',
  'Checklist': 'Tasks the ticket starts with. Set these up under Task Templates.',
  'Product line': 'Which product this is about. Picking one can add fields specific to it.',
  'Status': 'Where the ticket stands. Resolved and Closed ask for a note.',

  // Product and schedule
  'Product / Service': 'The product or service involved.',
  'Version': 'Which version they are running, if you know it.',
  'Department': 'The customer department this came from.',
  'Location': 'The site or building, if it matters.',
  'Due date': 'When this needs to be done by.',
  'Estimated hours': 'Your best guess at how long this will take.',

  // People
  'Assignees (first = primary)': 'Who works this ticket. The first person listed owns it.',
  'Team': 'Assign to a whole team. Everyone on it gets the emails.',
  'On behalf of (staff member)': 'A coworker you are filing this for. Not the customer.',
  'Organization': 'The company this ticket is for. Collaborators can only be people from here.',
  'Reporter (CRM contact)': 'The person who reported the problem, if they are in your CRM.',
  'Customer contact (CRM)': 'The person who reported the problem, if they are in your CRM.',
  'Reporter email (one-off)': 'Use this when the reporter is not in your CRM. They get the ticket emails.',
  'Reporter name': 'Their name, for the emails and the ticket.',

  // Technical
  'Browser': 'Which browser they were using, if it is a website problem.',
  'Device': 'The phone, tablet or computer involved.',
  'Operating System': 'Windows, macOS, iOS, Android, and the version if you know it.',
  'Reproducibility': 'How often you can make it happen again.',
  'Steps to reproduce': 'The exact steps someone else can follow to see the same thing.',

  // Closing out
  'Resolution': 'What actually fixed it. The customer can see this.',
  'Root cause': 'Why it happened, for your own records.',

  // CRM inbox and mailboxes
  'From mailbox': 'Which of your addresses this goes out from. Their reply comes back to the same conversation.',
  'To': 'Who receives it. Several addresses are fine, separated by commas.',
  'Cc': 'Anyone who should see a copy. They get every reply too.',
  'Message': 'The email as they will read it. Your mailbox signature is added underneath.',
  'Internal note': 'Only your team sees this. It is never emailed to them.',
  'Forward to': 'Send the whole thread to someone by email — a coworker, a partner. They can reply straight into it.',
  'Owner': 'The person responsible for answering. They get an email when it is handed to them.',
  'Address': 'The email address customers write to, e.g. sales@yourcompany.com. Point it at this server the same way as your ticket address.',
  'Mailbox name': 'What your team sees, e.g. Sales or Info.',
  'Sender name': 'The name their mail client shows, e.g. CozziTech Sales. Replies show the staff name in front of it.',
  'Send from address': 'Leave blank to send from the system address. Set it only if this address is verified in Postmark, or sends will fail.',
  'Signature': 'Added to the bottom of every email sent from this mailbox.',
  'Notify': 'Who gets an email heads-up on every new message. Owners are always told about their own conversations.',
  'Default owner': 'Who new conversations in this mailbox belong to. Leave blank to triage them from Unassigned.',
  'Auto-reply': 'Send an automatic acknowledgement to people writing in for the first time. Never sent to replies.',
  'Auto-reply text': 'The acknowledgement they receive. Keep it short; your real answer comes later.',
  'Thread window (days)': 'A second email from the same person within this many days joins their open conversation, even without a reply tag. 0 turns this off.',
  'Spam threshold': 'Mail scoring above this is quarantined. 7 is a sensible start; lower catches more, higher lets more through.',
  'Always allow': 'Senders or domains that skip spam scoring entirely, one per line.',
  'Blocked senders': 'Senders or domains whose mail is dropped before it reaches the inbox, one per line.',
  'Organization name': 'The company they work for. Matched by name if it already exists.',
  'Link existing': 'Pick a record that is already in the CRM instead of creating another one.',
  'Stage name': 'A step in your sales process, e.g. Qualified or Proposal sent.',
  'Win chance %': 'How likely a deal at this stage is to close. Used for forecasting.',
  'Color': 'The dot shown next to this stage on the board.',
  'Deal title': 'What you are selling them, in a few words.',
  'Stage': 'Where this deal is in your pipeline right now.',
  'Value': 'What the deal is worth if it closes.',
};

/** The hover text for a field label, or undefined when there is nothing useful to add. */
export function fieldHelp(label: string): string | undefined {
  return HELP[label] ?? HELP[label.replace(/\s*\*$/, '')];
}
